import React, { useState } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  X,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowRight,
  User,
  Calendar,
  DollarSign,
  FileText,
  History,
  RefreshCw,
  Loader2,
  AlertTriangle,
} from 'lucide-react';

/**
 * RefundDetails - Modal/Panel for viewing refund details and approval history
 *
 * Props:
 * - refund: object - The refund to display
 * - onClose: function - Called when modal is closed
 * - onApprove: function - Called when approve button is clicked (for approvers)
 * - onReject: function - Called when reject button is clicked (for approvers)
 * - canApprove: boolean - Whether current user can approve this refund
 * - currentUserId: string - Current user's ID (to check if they already approved)
 * - onRefundUpdated: function - Called when refund is updated (e.g., credit note retry)
 * - isAdmin: boolean - Whether current user is an admin (can retry credit note)
 */
const RefundDetails = ({
  refund,
  onClose,
  onApprove,
  onReject,
  canApprove = false,
  currentUserId = null,
  onRefundUpdated = null,
  isAdmin = false,
}) => {
  const [retryingCreditNote, setRetryingCreditNote] = useState(false);
  const [creditNoteError, setCreditNoteError] = useState(null);
  const [creditNoteSuccess, setCreditNoteSuccess] = useState(false);

  if (!refund) return null;

  const statusConfig = {
    pending_approval: {
      label: 'Pending Approval',
      color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      icon: Clock,
      description: 'Awaiting first approval',
    },
    approved: {
      label: 'First Approval Received',
      color: 'bg-blue-100 text-blue-800 border-blue-300',
      icon: CheckCircle,
      description: 'Awaiting second approval for processing',
    },
    pending_second_approval: {
      label: 'Pending Second Approval',
      color: 'bg-orange-100 text-orange-800 border-orange-300',
      icon: Clock,
      description: 'Requires one more approval before processing',
    },
    processing: {
      label: 'Processing',
      color: 'bg-purple-100 text-purple-800 border-purple-300',
      icon: ArrowRight,
      description: 'Refund is being processed through Stripe',
    },
    completed: {
      label: 'Completed',
      color: 'bg-green-100 text-green-800 border-green-300',
      icon: CheckCircle,
      description: 'Refund has been processed successfully',
    },
    rejected: {
      label: 'Rejected',
      color: 'bg-red-100 text-red-800 border-red-300',
      icon: XCircle,
      description: 'Refund request was rejected',
    },
    failed: {
      label: 'Failed',
      color: 'bg-red-100 text-red-800 border-red-300',
      icon: AlertCircle,
      description: 'Refund processing failed',
    },
  };

  const reasonLabels = {
    duplicate_charge: 'Duplicate Charge',
    service_not_provided: 'Service Not Provided',
    billing_error: 'Billing Error',
    customer_request: 'Customer Request',
    subscription_cancellation: 'Subscription Cancellation',
    other: 'Other',
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount / 100);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleRetryCreditNote = async () => {
    if (retryingCreditNote) return;

    setRetryingCreditNote(true);
    setCreditNoteError(null);
    setCreditNoteSuccess(false);

    try {
      const response = await axios.post(
        `${getApiBase()}/admin/billing/refunds/${refund.id}/retry-credit-note`,
        {},
        { headers: getAuthHeaders() }
      );

      setCreditNoteSuccess(true);

      // Notify parent to refresh refund data
      if (onRefundUpdated) {
        setTimeout(() => onRefundUpdated(), 1500);
      }
    } catch (err) {
      console.error('Error retrying credit note:', err);
      setCreditNoteError(err.response?.data?.detail || 'Failed to create credit note');
    } finally {
      setRetryingCreditNote(false);
    }
  };

  // Check if credit note can be retried
  const canRetryCreditNote =
    isAdmin &&
    ['succeeded', 'completed', 'approved'].includes(refund.status) &&
    !refund.xero_credit_note_id &&
    (refund.credit_note_status === 'failed' || refund.credit_note_status === 'skipped');

  const status = statusConfig[refund.status] || {
    label: refund.status,
    color: 'bg-gray-100 text-gray-800 border-gray-300',
    icon: AlertCircle,
    description: '',
  };
  const StatusIcon = status.icon;

  // Check if current user has already approved
  const userAlreadyApproved =
    currentUserId &&
    (refund.first_approved_by_id === currentUserId ||
      refund.second_approved_by_id === currentUserId);

  // Check if user is the requester (can't approve own request)
  const isRequester = currentUserId && refund.requested_by_id === currentUserId;

  // Determine if approval actions should be shown
  const showApprovalActions =
    canApprove &&
    !userAlreadyApproved &&
    !isRequester &&
    ['pending_approval', 'pending_second_approval'].includes(refund.status);

  // Build approval timeline
  const approvalTimeline = [];

  if (refund.created_at) {
    approvalTimeline.push({
      action: 'Requested',
      actor: refund.requested_by_name || 'Unknown',
      timestamp: refund.created_at,
      icon: FileText,
      color: 'text-gray-600',
    });
  }

  if (refund.first_approved_at) {
    approvalTimeline.push({
      action: 'First Approval',
      actor: refund.first_approved_by_name || 'Unknown',
      timestamp: refund.first_approved_at,
      icon: CheckCircle,
      color: 'text-green-600',
    });
  }

  if (refund.second_approved_at) {
    approvalTimeline.push({
      action: 'Second Approval',
      actor: refund.second_approved_by_name || 'Unknown',
      timestamp: refund.second_approved_at,
      icon: CheckCircle,
      color: 'text-green-600',
    });
  }

  if (refund.rejected_at) {
    approvalTimeline.push({
      action: 'Rejected',
      actor: refund.rejected_by_name || 'Unknown',
      timestamp: refund.rejected_at,
      notes: refund.rejection_notes,
      icon: XCircle,
      color: 'text-red-600',
    });
  }

  if (refund.processed_at) {
    approvalTimeline.push({
      action: 'Processed',
      actor: 'System',
      timestamp: refund.processed_at,
      icon: CheckCircle,
      color: 'text-green-600',
    });
  }

  if (refund.failed_at) {
    approvalTimeline.push({
      action: 'Failed',
      actor: 'System',
      timestamp: refund.failed_at,
      notes: refund.failure_reason,
      icon: AlertCircle,
      color: 'text-red-600',
    });
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b flex justify-between items-start">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Refund Details</h3>
            <p className="text-sm text-gray-500 mt-1">
              ID: {refund.id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status Banner */}
          <div className={`rounded-lg border p-4 ${status.color}`}>
            <div className="flex items-center">
              <StatusIcon className="w-5 h-5 mr-3" />
              <div>
                <p className="font-medium">{status.label}</p>
                {status.description && (
                  <p className="text-sm opacity-80">{status.description}</p>
                )}
              </div>
            </div>
          </div>

          {/* Amount */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Refund Amount</span>
              <span className="text-2xl font-semibold">
                {formatCurrency(refund.amount, refund.currency)}
              </span>
            </div>
            {refund.amount >= 10000 && (
              <p className="text-sm text-orange-600 mt-2">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Requires dual approval (amount &gt;= $100)
              </p>
            )}
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Reason</p>
              <p className="font-medium">
                {reasonLabels[refund.reason] || refund.reason}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Requested By</p>
              <p className="font-medium">{refund.requested_by_name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Payment ID</p>
              <p className="font-medium text-sm">{refund.payment_id}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Invoice ID</p>
              <p className="font-medium text-sm">{refund.invoice_id || '-'}</p>
            </div>
          </div>

          {/* Reason Notes */}
          {refund.reason_notes && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Additional Notes</p>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {refund.reason_notes}
                </p>
              </div>
            </div>
          )}

          {/* External IDs */}
          {(refund.stripe_refund_id || refund.xero_credit_note_id) && (
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">External References</p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {refund.stripe_refund_id && (
                  <div>
                    <p className="text-gray-500">Stripe Refund ID</p>
                    <p className="font-mono text-xs">{refund.stripe_refund_id}</p>
                  </div>
                )}
                {refund.xero_credit_note_id && (
                  <div>
                    <p className="text-gray-500">Xero Credit Note ID</p>
                    <p className="font-mono text-xs">{refund.xero_credit_note_id}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Credit Note Status */}
          {['succeeded', 'completed', 'approved'].includes(refund.status) && (
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Credit Note Status</p>
              {creditNoteSuccess ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                    <span className="text-green-700">Credit note created successfully!</span>
                  </div>
                </div>
              ) : refund.credit_note_status === 'created' || refund.xero_credit_note_id ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                    <span className="text-green-700">Credit note created in Xero</span>
                  </div>
                </div>
              ) : refund.credit_note_status === 'failed' ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <AlertTriangle className="w-4 h-4 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <span className="text-red-700 font-medium">Credit note creation failed</span>
                      {refund.credit_note_error && (
                        <p className="text-sm text-red-600 mt-1">{refund.credit_note_error}</p>
                      )}
                      {creditNoteError && (
                        <p className="text-sm text-red-600 mt-1">{creditNoteError}</p>
                      )}
                      {canRetryCreditNote && (
                        <button
                          onClick={handleRetryCreditNote}
                          disabled={retryingCreditNote}
                          className="mt-2 px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                        >
                          {retryingCreditNote ? (
                            <>
                              <Loader2 className="w-3 h-3 animate-spin" />
                              Retrying...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-3 h-3" />
                              Retry Credit Note
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : refund.credit_note_status === 'skipped' ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <span className="text-amber-700">Credit note skipped</span>
                      {refund.credit_note_error && (
                        <p className="text-sm text-amber-600 mt-1">{refund.credit_note_error}</p>
                      )}
                      {creditNoteError && (
                        <p className="text-sm text-red-600 mt-1">{creditNoteError}</p>
                      )}
                      {canRetryCreditNote && (
                        <button
                          onClick={handleRetryCreditNote}
                          disabled={retryingCreditNote}
                          className="mt-2 px-3 py-1 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1"
                        >
                          {retryingCreditNote ? (
                            <>
                              <Loader2 className="w-3 h-3 animate-spin" />
                              Creating...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-3 h-3" />
                              Create Credit Note
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 text-gray-500 mr-2" />
                    <span className="text-gray-600">Credit note status unknown</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Approval Timeline */}
          {approvalTimeline.length > 0 && (
            <div className="border-t pt-4">
              <div className="flex items-center mb-3">
                <History className="w-4 h-4 text-gray-500 mr-2" />
                <p className="text-sm font-medium text-gray-700">Approval History</p>
              </div>
              <div className="space-y-3">
                {approvalTimeline.map((event, idx) => (
                  <div key={idx} className="flex items-start">
                    <event.icon className={`w-4 h-4 mt-0.5 mr-3 ${event.color}`} />
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <p className="text-sm font-medium">{event.action}</p>
                        <p className="text-xs text-gray-500">
                          {formatDateTime(event.timestamp)}
                        </p>
                      </div>
                      <p className="text-sm text-gray-600">{event.actor}</p>
                      {event.notes && (
                        <p className="text-sm text-gray-500 mt-1 italic">
                          "{event.notes}"
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warning Messages */}
          {userAlreadyApproved && ['pending_approval', 'pending_second_approval'].includes(refund.status) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-blue-600 mr-2" />
                <span className="text-blue-800">
                  You have already approved this refund. Another approver is needed.
                </span>
              </div>
            </div>
          )}

          {isRequester && ['pending_approval', 'pending_second_approval'].includes(refund.status) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
                <span className="text-yellow-800">
                  You cannot approve your own refund request.
                </span>
              </div>
            </div>
          )}

          {/* Approval Actions */}
          {showApprovalActions && (
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">
                Take Action
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => onApprove && onApprove(refund)}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center justify-center"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve Refund
                </button>
                <button
                  onClick={() => onReject && onReject(refund)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center justify-center"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject Refund
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RefundDetails;
